<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useRouter, type RouteLocationRaw } from 'vue-router';

import { api } from '../api/client';
import { companyDetailsTo, type CompanyRouteRef } from '../lib/company-route';
import { companyNoteDetailsTo, type CompanyNoteRouteRef } from '../lib/note-route';
import { taskDetailsTo } from '../lib/task-route';

type SearchResult =
  | { kind: 'company'; id: string; number?: number; title: string; archived?: boolean }
  | {
      kind: 'project';
      id: string;
      title: string;
      clientName: string;
      clientId: string;
      clientNumber?: number;
      archived: boolean;
      clientArchived?: boolean;
    }
  | {
      kind: 'note';
      id: string;
      number?: number;
      title: string;
      clientName: string;
      clientId: string;
      clientNumber?: number;
      preview: string;
      clientArchived?: boolean;
    }
  | {
      kind: 'task';
      number: number;
      title: string;
      done: boolean;
      clientName: string;
      clientNumber?: number;
      projectName: string;
      projectNumber?: number;
      assigneeName: string | null;
      clientArchived?: boolean;
    };

const KIND_ORDER = ['company', 'project', 'note', 'task'] as const;

const KIND_LABELS: Record<(typeof KIND_ORDER)[number], string> = {
  company: 'Компании',
  project: 'Проекты',
  note: 'Заметки',
  task: 'Задачи',
};

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();

const router = useRouter();
const query = ref('');
const results = ref<SearchResult[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const inputEl = ref<HTMLInputElement | null>(null);
let timer: ReturnType<typeof setTimeout> | undefined;
let requestId = 0;

function optionalNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function requiredNumber(value: unknown): number | null {
  const n = optionalNumber(value);
  return n ?? null;
}

function str(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  return '';
}

function inferKind(row: Record<string, unknown>): SearchResult['kind'] | null {
  const kind = row['kind'];
  if (kind === 'company' || kind === 'project' || kind === 'note' || kind === 'task') {
    return kind;
  }
  if (row['projectName'] !== undefined) return 'task';
  if (row['preview'] !== undefined) return 'note';
  if (row['clientNumber'] !== undefined && row['archived'] !== undefined) return 'project';
  if (row['clientNumber'] !== undefined && row['number'] !== undefined) return 'note';
  if (row['number'] !== undefined || row['id'] !== undefined) return 'company';
  return null;
}

function parseSearchResults(raw: unknown): SearchResult[] {
  if (!Array.isArray(raw)) return [];
  const out: SearchResult[] = [];

  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const kind = inferKind(row);

    if (kind === 'company') {
      const id = str(row['id']);
      const number = optionalNumber(row['number']);
      if (!id && number === undefined) continue;
      out.push({
        kind: 'company',
        id: id || String(number),
        number,
        title: str(row['title']),
        archived: Boolean(row['archived']),
      });
      continue;
    }

    if (kind === 'project') {
      const id = str(row['id']);
      const clientId = str(row['clientId']);
      if (!id) continue;
      out.push({
        kind: 'project',
        id,
        title: str(row['title']),
        clientName: str(row['clientName']),
        clientId: clientId || str(row['clientNumber']),
        clientNumber: optionalNumber(row['clientNumber']),
        archived: Boolean(row['archived']),
        clientArchived: Boolean(row['clientArchived']),
      });
      continue;
    }

    if (kind === 'note') {
      const id = str(row['id']);
      const number = optionalNumber(row['number']);
      const clientId = str(row['clientId']);
      const clientNumber = optionalNumber(row['clientNumber']);
      if (!id && number === undefined) continue;
      out.push({
        kind: 'note',
        id: id || String(number),
        number,
        title: str(row['title']),
        clientName: str(row['clientName']),
        clientId: clientId || String(clientNumber ?? ''),
        clientNumber,
        preview: str(row['preview']),
        clientArchived: Boolean(row['clientArchived']),
      });
      continue;
    }

    if (kind === 'task') {
      const number = requiredNumber(row['number']);
      if (number === null) continue;
      out.push({
        kind: 'task',
        number,
        title: str(row['title']),
        done: Boolean(row['done']),
        clientName: str(row['clientName']),
        clientNumber: optionalNumber(row['clientNumber']),
        projectName: str(row['projectName']),
        projectNumber: optionalNumber(row['projectNumber']),
        assigneeName:
          typeof row['assigneeName'] === 'string' ? row['assigneeName'] : null,
        clientArchived: Boolean(row['clientArchived']),
      });
    }
  }

  return out;
}

function companyRef(r: { id: string; number?: number }): CompanyRouteRef {
  if (r.number !== undefined) return { number: r.number, id: r.id };
  return { id: r.id };
}

function noteRef(r: { id: string; number?: number }): CompanyNoteRouteRef {
  if (r.number !== undefined) return { number: r.number, id: r.id };
  return { id: r.id };
}

function resultTo(r: SearchResult): RouteLocationRaw {
  switch (r.kind) {
    case 'company':
      return companyDetailsTo(companyRef(r));
    case 'project':
      return companyDetailsTo(
        r.clientNumber !== undefined
          ? { number: r.clientNumber, id: r.clientId }
          : { id: r.clientId },
      );
    case 'note':
      return companyNoteDetailsTo(
        r.clientNumber !== undefined
          ? { number: r.clientNumber, id: r.clientId }
          : { id: r.clientId },
        noteRef(r),
      );
    case 'task': {
      if (r.clientNumber === undefined || r.projectNumber === undefined) {
        return { name: 'task-details-legacy', params: { id: String(r.number) } };
      }
      return taskDetailsTo({
        company: { number: r.clientNumber },
        project: { number: r.projectNumber },
        task: { number: r.number },
      });
    }
  }
}

const grouped = computed(() => {
  const sections = KIND_ORDER.map((kind) => ({
    kind,
    label: KIND_LABELS[kind],
    items: results.value.filter((r) => r.kind === kind),
  })).filter((section) => section.items.length > 0);

  if (sections.length > 0) return sections;
  if (results.value.length > 0) {
    return [{ kind: 'task' as const, label: 'Результаты', items: results.value }];
  }
  return [];
});

watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      await nextTick();
      inputEl.value?.focus();
    } else {
      query.value = '';
      results.value = [];
      error.value = null;
    }
  },
);

watch(query, (q) => {
  if (timer) clearTimeout(timer);
  const term = q.trim();
  if (!term) {
    results.value = [];
    error.value = null;
    return;
  }
  timer = setTimeout(() => {
    void (async () => {
      const id = ++requestId;
      loading.value = true;
      error.value = null;
      try {
        const r = await api<{ results?: unknown }>(
          `/api/search?q=${encodeURIComponent(term)}`,
        );
        if (id !== requestId) return;
        results.value = parseSearchResults(r.results);
      } catch (err) {
        if (id !== requestId) return;
        results.value = [];
        error.value = err instanceof Error ? err.message : 'Не удалось выполнить поиск';
      } finally {
        if (id === requestId) loading.value = false;
      }
    })();
  }, 200);
});

function resultKey(r: SearchResult): string {
  switch (r.kind) {
    case 'company':
      return `company-${r.id}`;
    case 'project':
      return `project-${r.id}`;
    case 'note':
      return `note-${r.id}`;
    case 'task':
      return `task-${r.number}`;
  }
}

function resultMeta(r: SearchResult): string {
  const archivedSuffix = ' · в архиве';
  switch (r.kind) {
    case 'company':
      return r.archived ? `Компания${archivedSuffix}` : 'Компания';
    case 'project': {
      const base = r.archived ? `${r.clientName} · архив` : r.clientName;
      return r.clientArchived ? `${base}${archivedSuffix}` : base;
    }
    case 'note': {
      const base = r.preview ? `${r.clientName} · ${r.preview}` : r.clientName;
      return r.clientArchived ? `${base}${archivedSuffix}` : base;
    }
    case 'task': {
      const base = `${r.clientName} / ${r.projectName}${r.assigneeName ? ` · ${r.assigneeName}` : ''}`;
      return r.clientArchived ? `${base}${archivedSuffix}` : base;
    }
  }
}

async function onResultClick(r: SearchResult): Promise<void> {
  try {
    await router.push(resultTo(r));
  } catch {
    /* duplicate navigation */
  }
  emit('close');
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="search-popover__backdrop"
      @click.self="emit('close')"
    >
      <div
        class="search-popover__panel"
        role="dialog"
        aria-label="Поиск"
      >
        <input
          ref="inputEl"
          v-model="query"
          type="search"
          class="search-popover__input"
          placeholder="Поиск: компании, проекты, заметки, задачи…"
          @keydown.esc="emit('close')"
        />
        <p
          v-if="loading"
          class="hint search-popover__msg"
        >
          Поиск…
        </p>
        <p
          v-else-if="error"
          class="hint search-popover__msg search-popover__msg--error"
        >
          {{ error }}
        </p>
        <p
          v-else-if="query.trim() && results.length === 0"
          class="hint search-popover__msg"
        >
          Ничего не найдено.
        </p>
        <p
          v-else-if="!query.trim()"
          class="hint search-popover__msg"
        >
          Введите запрос, чтобы найти компании, проекты, заметки и задачи.
        </p>
        <ul
          v-else-if="grouped.length > 0"
          class="search-popover__results"
        >
          <template
            v-for="section in grouped"
            :key="section.kind"
          >
            <li class="search-popover__section-label">
              {{ section.label }}
            </li>
            <li
              v-for="r in section.items"
              :key="resultKey(r)"
            >
              <button
                type="button"
                class="search-popover__result"
                :class="{ 'is-done': r.kind === 'task' && r.done }"
                @click="void onResultClick(r)"
              >
                <span class="search-popover__result-title">{{ r.title || '(без названия)' }}</span>
                <span class="search-popover__result-meta">{{ resultMeta(r) }}</span>
              </button>
            </li>
          </template>
        </ul>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.search-popover__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 12vh;
  z-index: 1000;
}

.search-popover__panel {
  width: min(620px, 92vw);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.4);
  overflow: hidden;
}

.search-popover__input {
  width: 100%;
  padding: 0.9rem 1rem;
  border: none;
  border-bottom: 1px solid var(--border);
  background: transparent;
  color: var(--text);
  font-size: 1rem;
  outline: none;
}

.search-popover__msg {
  padding: 0.9rem 1rem;
  margin: 0;
}

.search-popover__msg--error {
  color: var(--danger, #c44);
}

.search-popover__results {
  list-style: none;
  margin: 0;
  padding: 0.3rem;
  max-height: 56vh;
  overflow-y: auto;
}

.search-popover__section-label {
  padding: 0.45rem 0.7rem 0.2rem;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
}

.search-popover__section-label:not(:first-child) {
  margin-top: 0.35rem;
  padding-top: 0.55rem;
  border-top: 1px solid var(--border);
}

.search-popover__result {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  width: 100%;
  text-align: left;
  padding: 0.55rem 0.7rem;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--text);
  cursor: pointer;
}

.search-popover__result:hover {
  background: var(--surface-hover);
}

.search-popover__result.is-done .search-popover__result-title {
  text-decoration: line-through;
  color: var(--muted);
}

.search-popover__result-title {
  font-size: 0.92rem;
}

.search-popover__result-meta {
  font-size: 0.78rem;
  color: var(--muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
